import React from 'react';
import {defineMessages} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';
import {getEndOfWeek, getStartOfWeek, momentToTimestampSeconds} from '@igp/shared/utils';

import {groups, useBoAgentAccessCheck} from '@access-control';
import {useAgentId} from '@features/account/hooks';
import {
    type PlayerEcologyWeeklyColumns,
    PlayerEcologyWeeklyColumnsTypeName,
    PlayerEcologyWeeklyDataGrid,
    PlayerEcologyWeeklyDownloadCsvButton,
    PlayerEcologyWeeklyQueryFilters,
    type PlayerEcologyWeeklyViewModelKeys,
    PlayerEcologyWeeklyViewModelKeysTypeName,
    usePlayerEcologyWeekly,
    usePlayerEcologyWeeklyColumnSettings,
} from '@features/player-ecology-weekly';

const localized = defineMessages({
    title: {
        id: 'PlayerEcologyWeekly_title',
        defaultMessage: 'Player Ecology Weekly Report',
    },
});

function PlayerEcologyWeeklyInner() {
    const viewType: ViewType = 'PlayerEcologyWeekly';
    const pinnedColumns: PlayerEcologyWeeklyColumns[] = ['playerUid', 'playerUsername'];

    const initialColumns: PlayerEcologyWeeklyColumns[] = [
        ...pinnedColumns,
        'agentUid',
        'agentUsername',
        'reportWeekStart.seconds',
        'country',
        'ecologySummary.stake_2_is_pro_nlhe',
        'ecologySummary.stake_4_is_pro_nlhe',
        'ecologySummary.stake_10_is_pro_nlhe',
        'ecologySummary.stake_20_is_pro_nlhe',
        'ecologySummary.stake_50_is_pro_nlhe',
        'ecologySummary.stake_100_is_pro_nlhe',
        'ecologySummary.stake_200_is_pro_nlhe',
        'ecologySummary.stake_400_is_pro_nlhe',
        'ecologySummary.is_pro_shortdeck',
        'ecologySummary.is_pro_zoom',
        'ecologySummary.ecology_flag',
    ];
    const mapper = useAutoMapper();

    const vmFields = mapper.mapArray<PlayerEcologyWeeklyColumns, PlayerEcologyWeeklyViewModelKeys>(
        initialColumns,
        PlayerEcologyWeeklyColumnsTypeName,
        PlayerEcologyWeeklyViewModelKeysTypeName
    );

    const boAgentPlayerId = useAgentId();
    const {
        items,
        rawItems,
        totalCount,
        searchFilter,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        filterString,
        handleFilterChange,
    } = usePlayerEcologyWeekly({
        fields: vmFields,
        viewType,
        defaultPaging: {page: 1, pageSize: 25},
        validateFilter: () => true,
        syncWithUrl: true,
        defaultFilters: [
            {key: 'report_week_date_from', value: momentToTimestampSeconds(getStartOfWeek(1))},
            {key: 'report_week_date_to', value: momentToTimestampSeconds(getEndOfWeek(1))},
        ],
        hiddenFilters: [
            {key: 'default_referrer_id', value: boAgentPlayerId},
            {key: 'default_is_downstream', value: true},
        ],
        defaultSorting: [{field: 'reportWeekStart.seconds', sort: 'desc'}],
    });

    const {visibleColumns, onVisibleColumnsChange} = usePlayerEcologyWeeklyColumnSettings({
        defaultColumns: initialColumns,
        pinnedColumns,
        gridId: viewType,
    });

    const isAgent = useBoAgentAccessCheck(playerEcologyWeeklyAgentResource);
    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <PlayerEcologyWeeklyDownloadCsvButton
                            columns={visibleColumns}
                            filter={filterString}
                            filename={localized.title}
                            total={totalCount}
                            hasSummary
                        />
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems primary>
                            <PlayerEcologyWeeklyQueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['text', 'agent', 'countries', 'reportWeekDate']}
                                viewType={viewType}
                                defaultPlayerId={boAgentPlayerId}
                                isAgent={isAgent}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <PlayerEcologyWeeklyDataGrid
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{sortModel: searchFilter.sorting}}
                    onSortChange={handleSortChange}
                    configurableColumns={{
                        pinnedColumns,
                        visibleColumns,
                        onVisibleColumnsChange,
                    }}
                    columns={initialColumns}
                />
            }
        />
    );
}

export const playerEcologyWeeklyAgentResource = defineAccessControlledResource({
    name: 'PlayerEcologyWeeklyAgentResource',
    rules: new GroupAccessRule([groups.agentsGroup]),
});

export const PlayerEcologyWeekly = withTraceErrorBoundary(PlayerEcologyWeeklyInner, ErrorModal);
